import { useMemo, useState } from 'react';

import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FieldTypes } from 'constants/field-types-enum';
import { useToast } from 'hooks/toast';
import { usePortalUploadActions } from 'hooks/upload-actions';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import RefereeFormWrapper from 'components/Form/RefereeForm/RefereeFormWrapper';
import { FormMessage } from 'components/FormComponents';
import { Loading } from 'components/Loading/Loading';

import {
  useGetDashboardQuery,
  useGetRefereeFormQuery,
  useReplaceRefereeMutation,
} from 'api/candidate-portal-api';
import { selectPortalAuth } from 'api/candidate-portal-slice';

import CandidatePortalPage from './CandidatePortalPage/CandidatePortalPage';
import ReplaceRefereeUnavailable from './ReplaceRefereeUnavailable';

const REPLACE_REASON_FIELD_ID = '__replace_reason__';

const CandidateReplaceReferee = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToast();
  const { candidateId, refereeId } = useParams();
  const { isAuthenticated, checked } = useSelector(selectPortalAuth);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const { data: dashboard, isLoading: isLoadingDashboard } = useGetDashboardQuery(undefined, {
    skip: !checked || !isAuthenticated || !candidateId,
  });

  const {
    data: refereeForm,
    isLoading: isLoadingForm,
    error: formError,
  } = useGetRefereeFormQuery(refereeId, {
    skip: !checked || !isAuthenticated || !refereeId,
  });

  const [replaceReferee] = useReplaceRefereeMutation();
  const { getUpload, signUpload, createUpload, deleteUpload } = usePortalUploadActions();

  const dashboardPath = `/portal/dashboard/${candidateId}`;
  const brand = dashboard?.brand;
  const candidate = dashboard?.candidate;

  const formBrand = useMemo(
    () =>
      brand
        ? {
            ...brand,
            brand_logo: brand.logo_url,
            brand_name: brand.name,
          }
        : {},
    [brand],
  );

  const brandStyle = brand?.color_button
    ? {
        '--brand-button-bg': brand.color_button,
        '--brand-button-text': brand.color_button_text,
        '--brand-link-color': brand.color_link,
      }
    : {};

  const replacingReferee = useMemo(() => {
    if (!dashboard?.checks || !refereeId) return null;
    for (const check of dashboard.checks) {
      const referee = check.referees?.find(item => String(item.id) === String(refereeId));
      if (referee) {
        return { referee, check };
      }
    }
    return null;
  }, [dashboard, refereeId]);

  const compareEmails = useMemo(() => {
    if (!replacingReferee) return [];
    const { check, referee } = replacingReferee;
    const otherActiveReferees = (check.referees || []).filter(
      item => item.id !== referee.id && !item.replaced_by && !item.canceled && !item.deleted,
    );
    return [candidate?.email, ...otherActiveReferees.map(item => item.email)].filter(Boolean);
  }, [replacingReferee, candidate?.email]);

  const compareContacts = useMemo(() => {
    if (!replacingReferee) return [];
    const { check, referee } = replacingReferee;
    const otherActiveReferees = (check.referees || []).filter(
      item => item.id !== referee.id && !item.replaced_by && !item.canceled && !item.deleted,
    );
    return [
      candidate?.contact_number,
      ...otherActiveReferees.map(item => item.contact_number),
    ].filter(Boolean);
  }, [replacingReferee, candidate?.contact_number]);

  const textParams = useMemo(
    () => ({
      candidate: candidate?.first_name,
      candidateFirstName: candidate?.first_name,
      candidateFullName: candidate?.name,
      candidateLastName: candidate?.last_name,
      job: candidate?.job_role,
      companyName: brand?.name || dashboard?.company?.name,
    }),
    [candidate, brand?.name, dashboard?.company?.name],
  );

  const questions = useMemo(() => {
    if (!refereeForm?.fields) return null;
    const fields = [...refereeForm.fields];
    const maxOrder = Math.max(0, ...fields.map(field => Number(field.order) || 0));
    fields.push({
      id: REPLACE_REASON_FIELD_ID,
      type: FieldTypes.Text,
      text: t('candidatePortal.referees.fields.reason'),
      placeholder: t('candidatePortal.referees.fields.reasonPlaceholder'),
      required: true,
      order: maxOrder + 1,
    });
    return fields;
  }, [refereeForm, t]);

  const goToDashboard = () => navigate(dashboardPath, { replace: true });

  const handleSubmit = async answers => {
    setSubmitting(true);
    setError(false);
    const reason = answers[REPLACE_REASON_FIELD_ID] || '';
    const formAnswers = { ...answers };
    delete formAnswers[REPLACE_REASON_FIELD_ID];

    try {
      await replaceReferee({
        refereeId,
        params: { answers: formAnswers, reason },
      }).unwrap();
      showToast(
        t('candidatePortal.referees.replaceSuccessTitle'),
        t('candidatePortal.referees.replaceSuccessMessage'),
        'success',
      );
      navigate(dashboardPath);
    } catch (submitError) {
      setError(true);
      showToast(
        t('candidatePortal.referees.errorTitle'),
        submitError?.data?.error?.log || t('candidatePortal.referees.replaceErrorMessage'),
        'error',
      );
      setSubmitting(false);
    }
  };

  const isLoading = !checked || isLoadingDashboard || isLoadingForm;

  const replaceUnavailable =
    !isLoading &&
    !!dashboard &&
    (!!formError || !replacingReferee || !replacingReferee.referee.is_editable);

  if (isLoading) {
    return (
      <CandidatePortalPage showFooter={false} showHeader={false}>
        <Loading active />
      </CandidatePortalPage>
    );
  }

  if (replaceUnavailable) {
    return <ReplaceRefereeUnavailable onContinue={goToDashboard} />;
  }

  if (formError || !replacingReferee || !questions) {
    return (
      <CandidatePortalPage candidate={candidate} brand={brand} showFooter={false}>
        <div className="container px-4 py-8">
          <FormMessage
            message={
              formError?.data?.error?.log || t('candidatePortal.referees.replaceErrorMessage')
            }
          />
          <button
            type="button"
            onClick={goToDashboard}
            className="mt-4 text-xl font-medium text-text-primary underline bg-transparent border-0 cursor-pointer">
            {t('common.cancel')}
          </button>
        </div>
      </CandidatePortalPage>
    );
  }

  return (
    <CandidatePortalPage candidate={candidate} brand={brand} showFooter={false} showHeader={false}>
      <Loading active={submitting} />
      <div className="fixed inset-0 z-[60] flex flex-col bg-white" style={brandStyle}>
        <header className="shrink-0 h-20 px-6 sm:px-8 border-b border-border-primary bg-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.name || ''}
                className="h-10 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <img
                src="/assets/images/checkmate/icon-label.svg"
                alt={t('candidatePortal.checkmateLogo')}
                className="h-8 w-auto"
              />
            )}
            <h1 className="text-2xl font-medium text-text-primary m-0 truncate">
              {t('candidatePortal.referees.replaceModalTitle')}
            </h1>
          </div>
          <button
            type="button"
            onClick={goToDashboard}
            aria-label={t('common.close')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border-primary bg-transparent text-text-primary text-xl font-medium cursor-pointer hover:bg-surface-secondary transition-colors shrink-0">
            <HugeiconsIcon icon={Cancel01Icon} size={18} />
            {t('common.cancel')}
          </button>
        </header>

        <div className="flex-1 min-h-0 relative">
          {error && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-full max-w-xl px-4">
              <FormMessage message={t('candidatePortal.referees.replaceErrorMessage')} />
            </div>
          )}
          <RefereeFormWrapper
            formMessage={refereeForm?.message}
            questions={questions}
            logicJumps={refereeForm.logic_jumps}
            rules={refereeForm.rules}
            candidateId={candidate?.id}
            country={dashboard?.company?.country}
            brand={formBrand}
            textParams={textParams}
            getCandidateUpload={getUpload}
            deleteCandidateUpload={deleteUpload}
            signCandidateUpload={signUpload}
            createCandidateUpload={createUpload}
            onSubmit={handleSubmit}
            formId={refereeForm?.id}
            includeCandidateReferenceValidation
            candidateEmail={compareEmails}
            emailCompareErrorMessage={t('candidatePortal.referees.fields.emailCompareErrorMessage')}
            candidateContact={compareContacts}
            contactCompareErrorMessage={t(
              'candidatePortal.referees.fields.contactCompareErrorMessage',
            )}
            hideHeader
            height="100%"
          />
        </div>
      </div>
    </CandidatePortalPage>
  );
};

export default CandidateReplaceReferee;
